/* eslint-disable no-unused-vars */
export type ApiField = string | number | null | boolean | { field: string };

function isValidField(val: any): boolean {
  return val || val === 0;
}

function addParentheses(val: string): string {
  // empty value: return empty string
  if (!val) return '';
  const firstChar = val[0];
  const lastChar = val[val.length - 1];
  const innerString = val.substring(1, val.length - 2);

  // no parentheses in the end or in the beginning => add them
  if (firstChar !== '(' || lastChar !== ')') return `(${val})`;

  // only add parens if not present
  let openParens = 1;
  for (const char of innerString) {
    if (char === '(') openParens += 1;
    else if (char === ')') openParens -= 1;

    // beginning parentheses have been closed => add parentheses
    if (openParens < 1) return `(${val})`;
  }
  return val;
}

function serializeApiField(field: ApiField): string {
  switch (typeof field) {
    case 'number':
      return `${field}`;
    case 'string':
      if (field.match(/\d{4}-\d{2}-\d{2}/)) return field;
      return `'${field.replaceAll('\'', '\'\'')}'`;
    case 'object':
      if (field === null) return 'null';
      return field.field;
    case 'boolean':
      return field ? 'true' : 'false';
    default:
      return '';
  }
}

function serializeModificationExpression(expr: ApiModificationExpression): string {
  return `${expr[0]}${serializeExpression(expr[1])}`;
}

function serializeCombinationExpression(expr: ApiCombinationExpression): string {
  if (expr.length === 1) return '';
  const combinator = expr[0];
  return addParentheses(`${expr.slice(1).map((e) => serializeExpression(e)).filter((e) => !!e).join(combinator)}`);
}

function serializeComparisonExpression([combinator, ...parameters]: ApiComparisonExpression): string {
  const mappedExpr = parameters.map((e) => serializeExpression(e)).filter((e) => isValidField(e));
  if (mappedExpr.length < parameters.length) return '';
  return addParentheses(`${mappedExpr.join(combinator)}`);
}

function serializeSimpleFunctionExpression([funcName, ...parameters]: ApiSimpleFunctionExpression): string {
  return `${funcName}(${parameters.map((e) => serializeExpression(e)).join(', ')})`;
}

function serializeNestedFunctionExpression([field, funcName, alias, internalExpr]: ApiNestedFunctionExpression): string {
  const serializedInternalExpression = serializeExpression(internalExpr);
  if (!serializedInternalExpression) return '';
  return `${field}/${funcName}(${alias}: ${serializedInternalExpression})`;
}

function expressionIsCombination(expr: ApiExpression): expr is ApiCombinationExpression {
  if (!Array.isArray(expr)) return false;
  return Object.values(ApiCombination).includes(expr[0] as ApiCombination);
}

function expressionIsComparison(expr: ApiExpression): expr is ApiComparisonExpression {
  if (!Array.isArray(expr)) return false;
  return Object.values(ApiComparison).includes(expr[0] as ApiComparison);
}

function expressionIsModification(expr: ApiExpression): expr is ApiModificationExpression {
  if (!Array.isArray(expr)) return false;
  return Object.values(ApiModification).includes(expr[0] as ApiModification);
}

function expressionIsSimpleFunction(expr: ApiExpression): expr is ApiSimpleFunctionExpression {
  if (!Array.isArray(expr)) return false;
  return Object.values(ApiSimpleFunction).includes(expr[0] as ApiSimpleFunction);
}

function expressionIsNestedFunction(expr: ApiExpression): expr is ApiNestedFunctionExpression {
  if (!Array.isArray(expr)) return false;
  return Object.values(ApiNestedFunction).includes(expr[1] as ApiNestedFunction);
}

export function serializeExpression(expr: ApiExpression) {
  if (expressionIsModification(expr)) return serializeModificationExpression(expr);
  if (expressionIsComparison(expr)) return serializeComparisonExpression(expr);
  if (expressionIsCombination(expr)) return serializeCombinationExpression(expr);
  if (expressionIsSimpleFunction(expr)) return serializeSimpleFunctionExpression(expr);
  if (expressionIsNestedFunction(expr)) return serializeNestedFunctionExpression(expr);
  return serializeApiField(expr);
}

export interface Query {
  filters?: ApiExpression | null,
  sorts?: ApiSorts | null,
  limit?: number | null,
  skip?: number | null,
  select?: Set<string> | null,
  expand?: Record<string, Query | null> | null,
  isRoot?: boolean,
  apply?: ApiExpression | null,
  queryFields?: Record<string, string>;
}

export function serializeQuery({
  filters,
  sorts,
  limit,
  skip,
  select,
  expand,
  isRoot = true,
  apply,
  queryFields,
}: Query): string[] {
  const queryStrings = [];

  if (filters) {
    let filterQuery = serializeExpression(filters);
    if (isRoot) filterQuery = encodeURIComponent(filterQuery);
    if (filterQuery) queryStrings.push(`$filter=${filterQuery}`);
  }

  if (sorts) {
    let sortQuery = sortObjectToQuery(sorts);
    if (isRoot) sortQuery = encodeURIComponent(sortQuery);
    if (sortQuery) queryStrings.push(`$orderby=${sortQuery}`);
  }

  if (limit) {
    queryStrings.push(`$top=${limit}`);
  }

  if (skip) {
    queryStrings.push(`$skip=${skip}`);
  }

  if (select) {
    let selectQuery = [...select].join(',');
    if (isRoot) selectQuery = encodeURIComponent(selectQuery);
    queryStrings.push(`$select=${selectQuery}`);
  }

  if (apply) {
    let applyQuery = serializeExpression(apply);
    if (isRoot) applyQuery = encodeURIComponent(applyQuery);
    if (applyQuery) queryStrings.push(`$apply=${applyQuery}`);
  }

  if (expand) {
    const expandQueryStrings = [];
    // eslint-disable-next-line guard-for-in
    for (const key in expand) {
      const query = expand[key];

      if (query) {
        expandQueryStrings.push(`${key}(${serializeQuery({...query, isRoot: false}).join(';')})`);
      } else {
        expandQueryStrings.push(key);
      }
    }
    let expandQuery = expandQueryStrings.join(',');
    if (isRoot) expandQuery = encodeURIComponent(expandQuery);
    queryStrings.push(`$expand=${expandQuery}`);
  }

  if (queryFields) {
    // eslint-disable-next-line guard-for-in
    for (const key in queryFields) {
      const value = queryFields[key];

      if (value) {
        queryStrings.push(`${key}=${value}`);
      }
    }
  }
  return queryStrings;
}

export enum ApiSimpleFunction {
  Contains = 'contains',
  ToLower = 'tolower',
  Cast = 'cast',
  StartsWith = 'startswith',
  GroupBy = 'groupby',
  Aggregate = 'aggregate',
}

export enum ApiNestedFunction {
  Any = 'any',
  All = 'all',
}

export enum ApiCombination {
  And = ' and ',
  Or = ' or ',
}

export enum ApiModification {
  Negative = '-',
  Not = 'not ',
}

export enum ApiComparison {
  Equals = ' eq ',
  NotEquals = ' ne ',
  GreaterThan = ' gt ',
  GreaterThanEqual = ' ge ',
  LessThan = ' lt ',
  LessThanEqual = ' le ',
}

export type ApiSimpleFunctionExpression = [ApiSimpleFunction, ...ApiExpression[]];
export type ApiNestedFunctionExpression = [field: string, func: ApiNestedFunction, alias: string, expr: ApiExpression];
export type ApiModificationExpression = [ApiModification, ApiExpression];
export type ApiComparisonExpression = [
  ApiComparison,
  ApiExpression,
  ApiExpression
];
export type ApiCombinationExpression = [ApiCombination, ...ApiExpression[]];
export type ApiExpression
  = ApiCombinationExpression |
  ApiComparisonExpression |
  ApiModificationExpression |
  ApiSimpleFunctionExpression |
  ApiNestedFunctionExpression |
  ApiField;


export enum ApiSortOrder {
  Asc = 'asc',
  Desc = 'desc',
}

export type ApiSortItem = ApiSortOrder;
export type ApiSorts<T = string> = Array<[T, ApiSortItem]>

export function sortObjectToQuery<T extends ApiSorts>(object: T): string {
  const sortQueryElements = [];
  // eslint-disable-next-line guard-for-in
  for (const [key, current] of object) {
    if (current === undefined || current === null) continue;
    sortQueryElements.push(`${key} ${current}`);
  }

  return sortQueryElements.join(',');
}

export type ApiSortsDerived<T extends {}> = [keyof T, ApiSortItem][];
